import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useStore from "../../useStore";
import HeaderVolver from "../HeaderVolver/HeaderVolver";
import style from "./Favoritos.module.css";
import { obtenerFavoritos, eliminarFavorito, obtenerPerfil } from "../../repositorios/apiServer";

export default function Favoritos() {
  const [favoritos, setFavoritos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [filtroActivo, setFiltroActivo] = useState('productos');
  const [userData, setUserData] = useState(null);
  const [isMounted, setIsMounted] = useState(true);
  
  const userToken = useStore((state) => state.userToken);
  const navigate = useNavigate();

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  useEffect(() => {
    let abortController = new AbortController();
    
    const cargarPerfil = async () => {
      if (!userToken) {
        navigate("/login");
        return;
      }
      
      try {
        setCargando(true);
        
        const perfilData = await obtenerPerfil();
        
        if (!isMounted) return;
        
        if (perfilData && perfilData.user) {
          setUserData(perfilData.user);
        } else {
          navigate("/login");
        }
      } catch (error) {
        console.error("Error al cargar perfil:", error);
        if (isMounted) navigate("/login");
      }
    };

    cargarPerfil();
    
    return () => {
      abortController.abort();
    };
  }, [userToken, navigate, isMounted]);

  const cargarFavoritos = useCallback(async () => {
    if (!userToken) return;
    
    try {
      setCargando(true);
      setError(null);
      
      const favoritosData = await obtenerFavoritos(userToken);
      
      if (!isMounted) return;
      
      setFavoritos(favoritosData || []);
    } catch (error) {
      console.error("Error al cargar favoritos:", error);
      if (isMounted) {
        setError("No se pudieron cargar tus favoritos. Inténtalo de nuevo más tarde.");
      }
    } finally {
      if (isMounted) {
        setCargando(false);
      }
    }
  }, [userToken, isMounted]);

  useEffect(() => {
    cargarFavoritos();
  }, [userToken, cargarFavoritos]);

  const eliminarDeFavoritos = async (productoId) => {
    if (!userToken) return;
    
    try {
      setCargando(true);
      
      await eliminarFavorito(userToken, productoId);
      
      if (!isMounted) return;
      
      setFavoritos(prevFavoritos => 
        prevFavoritos.filter(p => {
          const id = p._id || p.id || "";
          return id.toString() !== productoId.toString();
        })
      );
    } catch (error) {
      console.error("Error al eliminar de favoritos:", error);
      if (isMounted) {
        setError("Error al eliminar de favoritos");
        
        try {
          const favoritosActualizados = await obtenerFavoritos(userToken);
          if (isMounted) {
            setFavoritos(favoritosActualizados || []);
          }
        } catch (reloadError) {
          console.error("Error al recargar favoritos:", reloadError);
        }
      }
    } finally {
      if (isMounted) {
        setCargando(false);
      }
    }
  };

  const esVino = (item) => {
    return item && (
      item.esVino || 
      item.VinosTiposCategoria || 
      item.VinosCategoria ||
      item.bodega ||
      item.anio ||
      item.do ||
      (item.descripcion && typeof item.descripcion === 'string' && 
       (item.descripcion.toLowerCase().includes('vino') || 
        item.descripcion.toLowerCase().includes('bodega') || 
        item.descripcion.toLowerCase().includes('d.o.')))
    );
  };

  const verProducto = (producto) => {
    const itemId = producto._id || producto.id;
    if (!itemId) return;
    
    if (esVino(producto)) {
      navigate(`/vinos/${itemId}`);
    } else {
      navigate(`/productos/${itemId}`);
    }
  };

  const formatearPrecio = (precio) => {
    if (precio === undefined || precio === null || precio === "") return "";
    return typeof precio === 'number' ? `${precio.toFixed(2)} €` : `${precio} €`;
  };

  const favoritosFiltrados = favoritos.filter(item => {
    if (filtroActivo === 'vinos') return esVino(item);
    if (filtroActivo === 'productos') return !esVino(item);
    return true;
  });

  return (
    <div className={style.favoritos_container}>
      <HeaderVolver
        tituloPrincipal="Mis" 
        tituloDestacado="Favoritos"
      />
      <div className={style.volver_container}>
        <button 
          className={style.boton_volver} 
          onClick={() => navigate("/carta")}
        >
          Volver
        </button>
      </div>
      <div className={style.favoritos_content}>
        <div className={style.filtros_container}>
          <button 
            className={`${style.boton_filtro} ${filtroActivo === 'productos' ? style.boton_filtro_activo : ''}`}
            onClick={() => setFiltroActivo('productos')}
          >
            COMIDA
          </button>
          <button 
            className={`${style.boton_filtro} ${filtroActivo === 'vinos' ? style.boton_filtro_activo : ''}`}
            onClick={() => setFiltroActivo('vinos')}
          >
            VINOS
          </button>
        </div>
        
        {cargando ? (
          <div className={style.loading}>Cargando favoritos...</div>
        ) : error ? (
          <div className={style.error}>{error}</div>
        ) : favoritos.length === 0 ? (
          <div className={style.empty_message}>
            <p>No tienes productos favoritos</p>
            <button 
              className={style.boton_explorar} 
              onClick={() => navigate("/carta")}
            >
              Explorar Productos
            </button>
          </div>
        ) : favoritosFiltrados.length === 0 ? (
          <div className={style.empty_message}>
            <p>No tienes {filtroActivo === 'vinos' ? 'vinos' : 'productos'} en tus favoritos</p>
            <button 
              className={style.boton_explorar} 
              onClick={() => navigate("/carta")}
            >
              Explorar Productos
            </button>
          </div>
        ) : (
          <div className={style.favoritos_grid}>
            {favoritosFiltrados.map((item, index) => {
              const itemId = item._id || item.id;
              
              return (
                <div key={itemId || index} className={style.favorito_item}>
                  <div className={style.producto_header}>
                    <h3 className={style.producto_nombre}>
                      {item.nombre || `Favorito ${index + 1}`}
                    </h3>
                    <p className={style.producto_precio}>{formatearPrecio(item.precio)}</p>
                  </div>
                  
                  {item.descripcion && item.descripcion !== "NaN" && (
                    <p className={style.producto_descripcion}>{item.descripcion}</p>
                  )}
                  
                  <div className={style.botones_container}>
                    <button 
                      className={style.boton_eliminar}
                      onClick={() => eliminarDeFavoritos(itemId)}
                    >
                      ELIMINAR
                    </button>
                    <button 
                      className={style.boton_compartir}
                      onClick={() => verProducto(item)}
                    >
                      COMPARTIR
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}